import axios from 'axios';
import { rateLimiter } from '../services/rateLimiter';
import apiClient, { fetchWithRetry } from '../services/api/apiClient';

export interface PriceDataPoint {
  timestamp: number;
  price: number;
}

export interface TaoStats {
  price: string;
  volume_24h: string;
  market_cap: string;
  circulating_supply: string;
  total_supply: string;
  max_supply: string;
}

export interface HistoricalStakeBalanceResponse {
  pagination: {
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
    next_page: number | null;
    prev_page: number | null;
  };
  data: Array<{
    block_number: number;
    timestamp: string;
    balance: string;
    balance_as_tao: string;
  }>;
}

// Helper function to safely serialize response data
const safeSerialize = (data: any): any => {
  try {
    return JSON.parse(JSON.stringify(data, (key, value) => {
      if (value === undefined || value === null) {
        return null;
      }
      if (typeof value === 'bigint') {
        return value.toString();
      }
      return value;
    }));
  } catch (error) {
    console.error('Error serializing data:', error);
    return null;
  }
};

class TaoStats {
  private apiKey: string;
  private readonly baseURL = 'https://api.taostats.io/api';
  private static instance: TaoStats | null = null;
  private offlineMode: boolean = false;
  private cachedData: Record<string, { data: any, timestamp: number }> = {};
  private readonly CACHE_EXPIRATION = 5 * 60 * 1000; // 5 minutes

  private constructor(apiKey: string) {
    this.apiKey = apiKey;
    
    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline.bind(this));
      window.addEventListener('offline', this.handleOffline.bind(this));
      this.offlineMode = !navigator.onLine;
    }
  }

  public static getInstance(apiKey?: string): TaoStats {
    if (!TaoStats.instance) {
      const key = apiKey || 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3';
      TaoStats.instance = new TaoStats(key);
    } else if (apiKey) {
      TaoStats.instance.auth(apiKey);
    }
    return TaoStats.instance;
  }

  auth(apiKey: string) {
    if (!apiKey) {
      throw new Error('Invalid API key provided');
    }
    this.apiKey = apiKey;
  }

  private getHeaders() {
    return {
      'accept': 'application/json',
      'Authorization': this.apiKey
    };
  }

  private handleOnline() {
    console.log('Network connection restored. TaoStats API client resuming normal operation.');
    this.offlineMode = false;
  }

  private handleOffline() {
    console.warn('Network connection lost. TaoStats API client switching to offline mode.');
    this.offlineMode = true;
  }

  private getCachedData<T>(cacheKey: string, defaultValue: T): T | null {
    const cachedItem = this.cachedData[cacheKey];
    if (cachedItem && (Date.now() - cachedItem.timestamp) < this.CACHE_EXPIRATION) {
      console.log(`Using cached data for ${cacheKey}`);
      return cachedItem.data as T;
    }
    return null;
  }

  private setCachedData(cacheKey: string, data: any): void {
    this.cachedData[cacheKey] = {
      data,
      timestamp: Date.now()
    };
  }

  private async makeRequest<T>(url: string, defaultValue: T, cacheKey?: string): Promise<T> {
    // Check if we're in offline mode
    if (this.offlineMode) {
      console.warn('TaoStats API client is in offline mode. Using cached data if available.');
      
      // Try to get from cache
      if (cacheKey) {
        const cachedData = this.getCachedData<T>(cacheKey, defaultValue);
        if (cachedData) {
          return cachedData;
        }
      }
      
      console.warn('No cached data available for this request.');
      return defaultValue;
    }
    
    // Check cache first if cacheKey is provided
    if (cacheKey) {
      const cachedData = this.getCachedData<T>(cacheKey, defaultValue);
      if (cachedData) {
        return cachedData;
      }
    }
    
    try {
      await rateLimiter.checkRateLimit();
      
      const response = await axios.get(url, { 
        headers: this.getHeaders(),
        timeout: 10000 // 10 second timeout
      });
      
      const serializedData = safeSerialize(response.data);
      if (!serializedData) {
        throw new Error('Failed to serialize response data');
      }
      
      // Cache the result if cacheKey is provided
      if (cacheKey) {
        this.setCachedData(cacheKey, serializedData);
      }
      
      return serializedData;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Handle network errors
        if (error.code === 'ECONNABORTED' || error.message.includes('Network Error')) {
          this.offlineMode = true;
          console.warn('Network error detected. Switching to offline mode.');
          
          // Try to get from cache
          if (cacheKey) {
            const cachedData = this.getCachedData<T>(cacheKey, defaultValue);
            if (cachedData) {
              return cachedData;
            }
          }
        }
        
        // Handle rate limit errors
        if (error.response?.status === 429) {
          console.warn('Rate limit exceeded. Using cached data if available.');
          
          // Try to get from cache
          if (cacheKey) {
            const cachedData = this.getCachedData<T>(cacheKey, defaultValue);
            if (cachedData) {
              return cachedData;
            }
          }
          
          return defaultValue;
        }
      }
      
      console.error('Request failed:', error);
      return defaultValue;
    }
  }

  async getValidator({ hotkey }: { hotkey: string }) {
    const defaultResponse = {
      pagination: {
        current_page: 1,
        per_page: 50,
        total_items: 0,
        total_pages: 1,
        next_page: null,
        prev_page: null
      },
      data: []
    };

    const cacheKey = `validator_${hotkey}`;
    return this.makeRequest(
      `${this.baseURL}/validator/latest/v1?hotkey=${encodeURIComponent(hotkey)}`,
      defaultResponse,
      cacheKey
    );
  }

  async getHistoricalStakeBalance({ 
    coldkey, 
    hotkey, 
    netuid,
    limit = 1000
  }: { 
    coldkey: string;
    hotkey: string;
    netuid: string;
    limit?: number;
  }): Promise<HistoricalStakeBalanceResponse> {
    const defaultResponse = {
      pagination: {
        current_page: 1,
        per_page: 50,
        total_items: 0,
        total_pages: 1,
        next_page: null,
        prev_page: null
      },
      data: []
    };

    const cacheKey = `stake_balance_${coldkey}_${hotkey}_${netuid}`;
    return this.makeRequest(
      `${this.baseURL}/dtao/stake_balance/history/v1?coldkey=${encodeURIComponent(coldkey)}&hotkey=${encodeURIComponent(hotkey)}&netuid=${netuid}&limit=${limit}`,
      defaultResponse,
      cacheKey
    );
  }

  async fetchTaoStats(): Promise<TaoStats> {
    const defaultResponse = {
      price: '0',
      volume_24h: '0',
      market_cap: '0',
      circulating_supply: '0',
      total_supply: '0',
      max_supply: '0'
    };

    const cacheKey = 'tao_stats';
    
    try {
      await rateLimiter.checkRateLimit();
      
      const response = await axios.get(
        `${this.baseURL}/price/latest/v1?asset=tao`,
        { headers: this.getHeaders() }
      );
      
      const serializedData = safeSerialize(response.data);
      if (!serializedData?.data?.[0]) {
        return defaultResponse;
      }

      const stats = serializedData.data[0];
      const result = {
        price: String(stats.price || '0'),
        volume_24h: String(stats.volume_24h || '0'),
        market_cap: String(stats.market_cap || '0'),
        circulating_supply: String(stats.circulating_supply || '0'),
        total_supply: String(stats.total_supply || '0'),
        max_supply: String(stats.max_supply || '0')
      };
      
      // Cache the result
      this.setCachedData(cacheKey, result);
      
      return result;
    } catch (error) {
      // Handle network errors
      if (axios.isAxiosError(error) && 
          (error.code === 'ECONNABORTED' || 
           error.message.includes('Network Error') || 
           error.response?.status === 429)) {
        
        this.offlineMode = true;
        console.warn('Network error or rate limit exceeded. Using cached data if available.');
        
        // Try to get from cache
        const cachedData = this.getCachedData<TaoStats>(cacheKey, defaultResponse);
        if (cachedData) {
          return cachedData;
        }
      }
      
      console.error('Failed to fetch TAO stats:', error);
      return defaultResponse;
    }
  }
}

// Create and export a singleton instance with a default API key
export const taostats = TaoStats.getInstance('tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3');

export async function fetchTaoStats(): Promise<TaoStats> {
  return taostats.fetchTaoStats();
}

export { TaoStats };